import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Flame,
  Menu,
  Moon,
  Sun,
  MessageSquare,
  BrainCircuit,
  BarChart,
  Download,
  X,
  Plus,
  Minus,
  Shuffle,
  Repeat,
  ArrowLeft,
  ArrowRight,
  ThumbsUp,
  ThumbsDown,
  Share2,
  Trash2,
} from 'lucide-react';

// --- MOCK DATA & AI SIMULATION ---

const courseData = {
  title: 'How To Make A Girlfriend',
  units: [
    {
      id: 'unit-1',
      title: 'Unit 1: The Confidence Foundation',
      topics: [
        { id: 'topic-1-1', title: 'Building Unshakeable Self-Worth' },
        { id: 'topic-1-2', title: 'The Mindset of Attraction' },
        { id: 'topic-1-3', title: 'Mastering First Impressions' },
      ],
    },
    {
      id: 'unit-2',
      title: 'Unit 2: The Art of Conversation',
      topics: [
        { id: 'topic-2-1', title: 'From "Hi" to Deep Connection' },
        { id: 'topic-2-2', title: 'Storytelling that Captivates' },
        { id: 'topic-2-3', title: 'Flirting with Wit and Charm' },
      ],
    },
    {
      id: 'unit-3',
      title: 'Unit 3: Dating Strategy',
      topics: [
        { id: 'topic-3-1', title: 'Crafting the Perfect Profile' },
        { id: 'topic-3-2', title: 'Planning Memorable Dates' },
        { id: 'topic-3-3', title: 'From Dating to Relationship' },
      ],
    },
  ],
};

const allTopics = courseData.units.flatMap((unit) => unit.topics.map((t) => ({ ...t, unitId: unit.id })));
const totalTopics = allTopics.length;
const MAX_CHAT_HISTORY = 50;

const aiContentGenerator = {
  async getTopicContent(topicId) {
    await new Promise((res) => setTimeout(res, 400));
    const topic = allTopics.find((t) => t.id === topicId) || {};
    return {
      explanation: `This module, "${topic.title}," delves into the core principles of social dynamics. We will explore how to cultivate a magnetic personality, not by changing who you are, but by amplifying your best qualities. The key is authenticity combined with proven psychological techniques that foster genuine connection.`,
      dos: [
        'Be genuinely curious about her',
        'Listen more than you talk',
        'Use open body language',
        'Share your passions confidently',
      ],
      donts: [
        'Use pickup lines',
        'Brag about money or possessions',
        'Be negative or complain',
        "Pretend to be someone you're not",
      ],
      image: `https://placehold.co/800x400/e9d5ff/4c1d95?text=${encodeURIComponent(topic.title)}`,
    };
  },
  async getWingmanReply(history) {
    await new Promise((res) => setTimeout(res, 1200));
    const replies = [
      "That's a great start! How about you add a question to keep the conversation going?",
      'A bit generic. Can you make it more personal to her profile?',
      'Confident and direct. I like it. Send it!',
      'Maybe ease into that. A little too strong for an opener.',
    ];
    return replies[Math.floor(Math.random() * replies.length)];
  },
  async getFlashcards(topicId) {
    await new Promise((res) => setTimeout(res, 300));
    return [
      {
        q: "What is the 'Observer Effect' in social situations?",
        a: 'The tendency for people to behave differently when they know they are being watched.',
      },
      { q: 'A key to active listening is to listen with the intent to...', a: '...understand, not just to reply.' },
      { q: "What is a 'cold read'?", a: "A high-probability guess about a person you've just met to build rapport." },
      {
        q: "What's the 'Five-Second Rule' for approaching?",
        a: 'If you see someone you want to talk to, you should approach them within five seconds to avoid overthinking.',
      },
    ];
  },
  async getSummary(topicId) {
    await new Promise((res) => setTimeout(res, 300));
    const topic = allTopics.find((t) => t.id === topicId) || {};
    return {
      points: [
        'Authenticity is your greatest asset.',
        'Confidence is a skill, not a trait.',
        'Focus on making connections, not just impressions.',
        'Action overcomes fear.',
      ],
      steps: [
        'Compliment one stranger today.',
        'Ask a colleague about their weekend and actively listen.',
        "Write down three things you're proud of.",
      ],
      quote: 'The future belongs to those who believe in the beauty of their dreams. - Eleanor Roosevelt',
    };
  },
};

// --- ZUSTAND STORE ---

const useCourseStore = create(
  persist(
    (set, get) => ({
      // UI State
      isHydrated: false,
      isSidebarCollapsed: false,
      isMobileMenuOpen: false,
      theme: 'light',
      activeModule: 'topic',
      fontSize: 16,

      // Course Progress State
      activeTopicId: allTopics[0].id,
      completedTopics: new Set(),
      streak: 0,
      lastCompletionDate: null,

      // Per-Topic Data
      notes: {},
      chatHistory: {},
      flashcardState: {},

      // Actions
      setHydrated: () => set({ isHydrated: true }),
      init: () => {
        const { lastCompletionDate } = get();
        if (lastCompletionDate) {
          const today = new Date().toDateString();
          const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toDateString();
          if (lastCompletionDate !== today && lastCompletionDate !== yesterday) {
            set({ streak: 0 });
          }
        }
      },
      toggleTheme: () => set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
      setMobileMenuOpen: (isOpen) => set({ isMobileMenuOpen: isOpen }),
      setActiveTopic: (topicId) => {
        const { activeTopicId: currentTopicId } = get();
        if (currentTopicId !== topicId) {
          set({ activeTopicId: topicId, activeModule: 'topic' });
        }
      },
      setActiveModule: (moduleId) => set({ activeModule: moduleId }),

      adjustFontSize: (amount) => set((state) => ({ fontSize: Math.max(12, Math.min(24, state.fontSize + amount)) })),

      markTopicComplete: () => {
        const { activeTopicId, completedTopics, lastCompletionDate, streak } = get();
        const newCompletedTopics = new Set(completedTopics);
        let newStreak = streak;
        const today = new Date().toDateString();

        if (!newCompletedTopics.has(activeTopicId)) {
          newCompletedTopics.add(activeTopicId);

          if (lastCompletionDate !== today) {
            const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toDateString();
            newStreak = lastCompletionDate === yesterday ? streak + 1 : 1;
          }
          set({ completedTopics: newCompletedTopics, streak: newStreak, lastCompletionDate: today });
        }
      },

      updateNote: (note) => set((state) => ({ notes: { ...state.notes, [state.activeTopicId]: note } })),

      addChatMessage: (message) =>
        set((state) => {
          const history = state.chatHistory[state.activeTopicId] || [];
          const newHistory = [...history, message];
          if (newHistory.length > MAX_CHAT_HISTORY) {
            newHistory.splice(0, newHistory.length - MAX_CHAT_HISTORY);
          }
          return { chatHistory: { ...state.chatHistory, [state.activeTopicId]: newHistory } };
        }),
      clearChatHistory: () =>
        set((state) => ({
          chatHistory: { ...state.chatHistory, [state.activeTopicId]: [] },
        })),

      updateFlashcardStatus: (cardIndex, status) =>
        set((state) => {
          const topicState = state.flashcardState[state.activeTopicId] || {
            known: new Set(),
            needsPractice: new Set(),
          };
          const newKnown = new Set(topicState.known);
          const newNeedsPractice = new Set(topicState.needsPractice);

          if (status === 'known') {
            newKnown.add(cardIndex);
            newNeedsPractice.delete(cardIndex);
          } else {
            newNeedsPractice.add(cardIndex);
            newKnown.delete(cardIndex);
          }

          return {
            flashcardState: {
              ...state.flashcardState,
              [state.activeTopicId]: { known: newKnown, needsPractice: newNeedsPractice },
            },
          };
        }),
    }),
    {
      name: 'course-progress-storage',
      storage: createJSONStorage(() => localStorage, {
        replacer: (key, value) => {
          if (value instanceof Set) {
            return {
              _type: 'set',
              value: Array.from(value),
            };
          }
          return value;
        },
        reviver: (key, value) => {
          if (value && value._type === 'set') {
            return new Set(value.value);
          }
          return value;
        },
      }),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) => !['isHydrated', 'isMobileMenuOpen', 'isSidebarCollapsed'].includes(key)
          )
        ),
      // CORRECTED: Added robust migration logic to handle old data formats in localStorage.
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.error('An error happened during hydration', error);
          }
          if (state) {
            // This function runs after the state is loaded from storage.
            // We add a defensive check here to migrate data from an old format if necessary.
            // This handles the case where localStorage has arrays instead of the expected Set objects.
            if (Array.isArray(state.completedTopics)) {
              state.completedTopics = new Set(state.completedTopics);
            }

            if (state.flashcardState) {
              Object.values(state.flashcardState).forEach((topicState) => {
                if (topicState && Array.isArray(topicState.known)) {
                  topicState.known = new Set(topicState.known);
                }
                if (topicState && Array.isArray(topicState.needsPractice)) {
                  topicState.needsPractice = new Set(topicState.needsPractice);
                }
              });
            }

            // After ensuring data integrity, we can call the initialization actions.
            state.setHydrated();
            state.init();
          }
        };
      },
    }
  )
);

// --- UI COMPONENTS ---

const LoadingSkeleton = () => (
  <div className="flex h-screen w-full items-center justify-center bg-gray-100 dark:bg-gray-900">
    <div className="flex flex-col items-center gap-4">
      <BrainCircuit className="h-16 w-16 animate-pulse text-pink-500" />
      <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">Loading Your Course...</p>
    </div>
  </div>
);

const Tooltip = ({ children, text }) => (
  <div className="relative group flex justify-center">
    {children}
    <span className="absolute left-full ml-4 w-auto p-2 min-w-max rounded-md shadow-md text-white bg-gray-900 dark:bg-black text-xs font-bold transition-all duration-100 scale-0 origin-left group-hover:scale-100 z-50">
      {text}
    </span>
  </div>
);

const Header = () => {
  const { theme, toggleTheme, streak, completedTopics, setMobileMenuOpen } = useCourseStore();
  const progress = totalTopics > 0 ? Math.round((completedTopics.size / totalTopics) * 100) : 0;

  return (
    <header className="fixed top-0 left-0 right-0 z-40 flex h-16 items-center justify-between border-b border-gray-200 bg-white/80 px-4 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/80">
      <div className="flex items-center gap-2">
        <button onClick={() => setMobileMenuOpen(true)} className="p-2 lg:hidden" aria-label="Open menu">
          <Menu />
        </button>
        <h1 className="hidden text-lg font-bold sm:block">{courseData.title}</h1>
      </div>

      <div className="flex-1 px-4 sm:px-8">
        <div className="mx-auto w-full max-w-sm">
          <div className="relative h-4 w-full rounded-full bg-gray-200 dark:bg-gray-700">
            <motion.div
              className="h-4 rounded-full bg-pink-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white mix-blend-difference">
              {progress}% Complete
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 dark:bg-gray-800">
          <Flame className="h-5 w-5 text-orange-500" />
          <span className="font-bold">{streak}</span>
        </div>
        <button
          onClick={toggleTheme}
          className="p-2"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={theme}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.2 }}>
              {theme === 'light' ? <Moon /> : <Sun />}
            </motion.div>
          </AnimatePresence>
        </button>
      </div>
    </header>
  );
};

const Sidebar = () => {
  const { isSidebarCollapsed, toggleSidebar, isMobileMenuOpen, setMobileMenuOpen } = useCourseStore();
  const [expandedUnits, setExpandedUnits] = useState(() =>
    Object.fromEntries(courseData.units.map((unit) => [unit.id, true]))
  );

  const sidebarVariants = {
    open: { width: 320, transition: { type: 'spring', stiffness: 300, damping: 30 } },
    collapsed: { width: 80, transition: { type: 'spring', stiffness: 300, damping: 30 } },
  };

  const mobileSidebarVariants = {
    open: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } },
    closed: { x: '-100%', transition: { type: 'spring', stiffness: 300, damping: 30 } },
  };

  const SidebarContent = () => {
    const { activeTopicId, setActiveTopic, completedTopics, isSidebarCollapsed } = useCourseStore();

    const handleTopicClick = (topicId) => {
      setActiveTopic(topicId);
      if (isMobileMenuOpen) setMobileMenuOpen(false);
    };

    const getUnitProgress = (unit) => {
      const unitTopics = unit.topics.map((t) => t.id);
      const completedInUnit = unitTopics.filter((id) => completedTopics.has(id)).length;
      return Math.round((completedInUnit / unitTopics.length) * 100);
    };

    return (
      <div className="flex h-full flex-col">
        <div
          className={`flex h-16 shrink-0 items-center border-b border-gray-200 px-4 dark:border-gray-700 ${
            isSidebarCollapsed ? 'justify-center' : 'justify-between'
          }`}>
          {!isSidebarCollapsed && <h2 className="text-lg font-bold">Course Outline</h2>}
          <button
            onClick={toggleSidebar}
            className="hidden p-2 lg:block"
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <ChevronLeft className={`transition-transform ${isSidebarCollapsed ? 'rotate-180' : ''}`} />
          </button>
          <button onClick={() => setMobileMenuOpen(false)} className="p-2 lg:hidden" aria-label="Close menu">
            <X />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2">
          {courseData.units.map((unit) => (
            <div key={unit.id} className="mb-2">
              <button
                onClick={() => setExpandedUnits((s) => ({ ...s, [unit.id]: !s[unit.id] }))}
                aria-expanded={expandedUnits[unit.id]}
                className="flex w-full items-center justify-between rounded-md p-2 text-left font-semibold hover:bg-gray-100 dark:hover:bg-gray-700">
                {isSidebarCollapsed ? (
                  <Tooltip text={unit.title}>
                    <BookOpen />
                  </Tooltip>
                ) : (
                  <>
                    <span className="truncate">{unit.title}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">{getUnitProgress(unit)}%</span>
                      <ChevronDown className={`transition-transform ${expandedUnits[unit.id] ? 'rotate-180' : ''}`} />
                    </div>
                  </>
                )}
              </button>
              <AnimatePresence>
                {expandedUnits[unit.id] && !isSidebarCollapsed && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pl-4 pt-1">
                    {unit.topics.map((topic) => (
                      <li key={topic.id}>
                        <button
                          onClick={() => handleTopicClick(topic.id)}
                          role="menuitem"
                          className={`flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors ${
                            activeTopicId === topic.id
                              ? 'bg-pink-100 font-semibold text-pink-600 dark:bg-pink-900/50 dark:text-pink-300'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}>
                          <CheckCircle
                            className={`h-5 w-5 flex-shrink-0 ${
                              completedTopics.has(topic.id) ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'
                            }`}
                          />
                          <span className="truncate">{topic.title}</span>
                        </button>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>
      </div>
    );
  };

  return (
    <>
      <motion.aside
        aria-label="Course Navigation"
        variants={sidebarVariants}
        animate={isSidebarCollapsed ? 'collapsed' : 'open'}
        className="hidden h-full flex-shrink-0 border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:block">
        <SidebarContent />
      </motion.aside>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            />
            <motion.aside
              role="dialog"
              aria-modal="true"
              variants={mobileSidebarVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed top-0 left-0 z-50 h-full w-80 border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:hidden">
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// --- MODULE COMPONENTS ---

const TopicModule = () => {
  const { activeTopicId, fontSize, adjustFontSize, notes, updateNote } = useCourseStore();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    aiContentGenerator.getTopicContent(activeTopicId).then((data) => {
      setContent(data);
      setIsLoading(false);
    });
  }, [activeTopicId]);

  const currentNote = notes[activeTopicId] || '';

  if (isLoading) return <div className="p-8 text-center">Loading topic...</div>;
  if (!content) return <div className="p-8 text-center text-red-500">Failed to load content.</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-3xl font-bold">{allTopics.find((t) => t.id === activeTopicId)?.title}</h2>
        <div className="flex items-center gap-2 rounded-full border bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
          <button
            onClick={() => adjustFontSize(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Decrease font size">
            <Minus size={16} />
          </button>
          <span className="text-sm font-medium w-6 text-center" aria-live="polite">
            {fontSize}
          </span>
          <button
            onClick={() => adjustFontSize(1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Increase font size">
            <Plus size={16} />
          </button>
        </div>
      </div>

      <div
        style={{ fontSize: `${fontSize}px` }}
        className="prose prose-lg dark:prose-invert max-w-none transition-all duration-300">
        <img src={content.image} alt="Topic illustration" className="w-full rounded-lg mb-6 shadow-md" />
        <p>{content.explanation}</p>

        <div className="my-8 grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/50">
            <h3 className="font-bold text-green-800 dark:text-green-300">Do's</h3>
            <ul className="mt-2">
              {content.dos.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/50">
            <h3 className="font-bold text-red-800 dark:text-red-300">Don'ts</h3>
            <ul className="mt-2">
              {content.donts.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <h3 className="mt-12">Personal Notes</h3>
        <textarea
          value={currentNote}
          onChange={(e) => updateNote(e.target.value)}
          placeholder="Write your thoughts and reflections here..."
          className="w-full h-40 p-4 rounded-lg border bg-gray-50 dark:bg-gray-800 dark:border-gray-700 focus:ring-2 focus:ring-pink-500"
        />
      </div>
    </div>
  );
};

const WingmanModule = () => {
  const { activeTopicId, chatHistory, addChatMessage, clearChatHistory } = useCourseStore();
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const currentHistory = chatHistory[activeTopicId] || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [currentHistory]);

  const handleSend = async (text) => {
    if (!text.trim() || isTyping) return;

    const userMessage = { role: 'user', text };
    addChatMessage(userMessage);

    setIsTyping(true);
    const aiReply = await aiContentGenerator.getWingmanReply([...currentHistory, userMessage]);
    setIsTyping(false);

    addChatMessage({ role: 'ai', text: aiReply });
  };

  const quickReplies = ['Critique my opener', 'How do I recover from being ghosted?', 'Suggest a flirty response'];

  return (
    <div className="flex h-full flex-col p-4 sm:p-6 md:p-8">
      <div className="mb-4 flex justify-between items-center">
        <div className="text-center flex-1">
          <h2 className="text-2xl font-bold">AI Wingman</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Practice your messages. Get instant feedback.</p>
        </div>
        {currentHistory.length > 0 && (
          <button
            onClick={clearChatHistory}
            className="p-2 text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors"
            aria-label="Clear chat history">
            <Trash2 size={18} />
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto rounded-lg bg-gray-100 p-4 dark:bg-gray-800 shadow-inner">
        {currentHistory.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
            <MessageSquare size={48} className="mb-4" />
            <p>
              You are a witty, confident, supportive dating coach. Help the user practice, critique, and improve their
              messages.
            </p>
            <p className="mt-2 font-semibold">Start by sending a message below.</p>
          </div>
        )}
        <div className="space-y-4">
          {currentHistory.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-end gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'ai' && (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-500 text-white flex-shrink-0">
                  <BrainCircuit size={18} />
                </div>
              )}
              <div
                className={`max-w-md rounded-2xl px-4 py-2 ${
                  msg.role === 'user'
                    ? 'rounded-br-lg bg-blue-500 text-white'
                    : 'rounded-bl-lg bg-white dark:bg-gray-700'
                }`}>
                {msg.text}
              </div>
            </motion.div>
          ))}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-end gap-2 justify-start">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-pink-500 text-white flex-shrink-0">
                <BrainCircuit size={18} />
              </div>
              <div className="flex items-center gap-1 rounded-2xl rounded-bl-lg bg-white dark:bg-gray-700 px-4 py-3">
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
                  className="h-2 w-2 rounded-full bg-gray-400"
                />
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut', delay: 0.1 }}
                  className="h-2 w-2 rounded-full bg-gray-400"
                />
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
                  className="h-2 w-2 rounded-full bg-gray-400"
                />
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <div className="mt-4">
        <div className="flex flex-wrap gap-2 mb-2">
          {quickReplies.map((reply) => (
            <button
              key={reply}
              onClick={() => handleSend(reply)}
              className="text-xs rounded-full px-3 py-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors">
              {reply}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (handleSend(inputValue), setInputValue(''))}
            placeholder="Type your message..."
            className="flex-1 rounded-lg border bg-white p-3 dark:border-gray-600 dark:bg-gray-900 focus:ring-2 focus:ring-pink-500"
          />
          <button
            onClick={() => (handleSend(inputValue), setInputValue(''))}
            className="rounded-lg bg-pink-500 p-3 text-white hover:bg-pink-600 transition-colors disabled:opacity-50"
            aria-label="Send message"
            disabled={isTyping}>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

const FlashcardsModule = () => {
  const { activeTopicId, flashcardState, updateFlashcardStatus } = useCourseStore();
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);

  const currentTopicState = flashcardState[activeTopicId] || { known: new Set(), needsPractice: new Set() };

  useEffect(() => {
    setIsLoading(true);
    aiContentGenerator.getFlashcards(activeTopicId).then((data) => {
      setCards(data);
      setCurrentIndex(0);
      setIsFlipped(false);
      setIsLoading(false);
    });
  }, [activeTopicId]);

  const handleShuffle = () => {
    setCards((c) => [...c].sort(() => Math.random() - 0.5));
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleReset = () => {
    aiContentGenerator.getFlashcards(activeTopicId).then((data) => setCards(data));
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleNav = (direction) => {
    setIsFlipped(false);
    setCurrentIndex((i) => {
      const newIndex = i + direction;
      if (newIndex < 0) return cards.length - 1;
      if (newIndex >= cards.length) return 0;
      return newIndex;
    });
  };

  const handleMark = (status) => {
    updateFlashcardStatus(currentIndex, status);
    setTimeout(() => handleNav(1), 200);
  };

  if (isLoading) return <div className="p-8 text-center">Loading flashcards...</div>;
  if (cards.length === 0) return <div className="p-8 text-center">No flashcards available for this topic.</div>;

  const card = cards[currentIndex];
  const cardStatus = currentTopicState.known.has(currentIndex)
    ? 'known'
    : currentTopicState.needsPractice.has(currentIndex)
    ? 'needsPractice'
    : null;

  return (
    <div className="flex h-full flex-col items-center justify-center p-4 sm:p-6 md:p-8">
      <h2 className="text-2xl font-bold mb-4">Flashcards</h2>
      <div className="w-full max-w-lg mb-4">
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-1">
          {currentIndex + 1} / {cards.length}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
          <div
            className="bg-pink-600 h-2.5 rounded-full"
            style={{ width: `${((currentIndex + 1) / cards.length) * 100}%` }}></div>
        </div>
      </div>

      <div className="w-full max-w-lg h-64 [perspective:1000px]">
        <motion.div
          className="relative w-full h-full [transform-style:preserve-3d]"
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.5 }}
          onClick={() => setIsFlipped(!isFlipped)}
          role="button"
          aria-label={isFlipped ? `Answer: ${card.a}` : `Question: ${card.q}`}>
          <div
            className={`absolute flex h-full w-full items-center justify-center rounded-xl p-6 text-center text-2xl font-semibold shadow-lg [backface-visibility:hidden] ${
              cardStatus === 'known'
                ? 'bg-green-100 dark:bg-green-900'
                : cardStatus === 'needsPractice'
                ? 'bg-red-100 dark:bg-red-900'
                : 'bg-white dark:bg-gray-700'
            }`}>
            {card.q}
          </div>
          <div className="absolute flex h-full w-full items-center justify-center rounded-xl bg-blue-100 p-6 text-center text-xl shadow-lg [transform:rotateY(180deg)] [backface-visibility:hidden] dark:bg-blue-900">
            {card.a}
          </div>
        </motion.div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleMark('needsPractice')}
            className="p-3 rounded-full bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-300 dark:hover:bg-red-900"
            aria-label="Mark as needs practice">
            <ThumbsDown />
          </button>
          <button
            onClick={() => handleMark('known')}
            className="p-3 rounded-full bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/50 dark:text-green-300 dark:hover:bg-green-900"
            aria-label="Mark as known">
            <ThumbsUp />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleNav(-1)}
            className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            aria-label="Previous card">
            <ArrowLeft />
          </button>
          <button
            onClick={() => handleNav(1)}
            className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            aria-label="Next card">
            <ArrowRight />
          </button>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleShuffle}
            className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            aria-label="Shuffle cards">
            <Shuffle />
          </button>
          <button
            onClick={handleReset}
            className="p-3 rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            aria-label="Reset deck">
            <Repeat />
          </button>
        </div>
      </div>
    </div>
  );
};

const SummaryModule = () => {
  const { activeTopicId } = useCourseStore();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const summaryRef = useRef(null);

  useEffect(() => {
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error(`Script load error for ${src}`));
        document.head.appendChild(script);
      });
    };

    Promise.all([
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'),
      loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'),
    ])
      .then(() => setScriptsLoaded(true))
      .catch((error) => console.error('Failed to load PDF scripts:', error));
  }, []);

  useEffect(() => {
    setIsLoading(true);
    aiContentGenerator.getSummary(activeTopicId).then((data) => {
      setSummary(data);
      setIsLoading(false);
    });
  }, [activeTopicId]);

  const handleDownload = () => {
    if (!scriptsLoaded || !summaryRef.current || isDownloading) return;
    setIsDownloading(true);

    const html2canvas = window.html2canvas;
    const { jsPDF } = window.jspdf;

    html2canvas(summaryRef.current, { backgroundColor: null, useCORS: true, scale: 2 })
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = imgWidth / imgHeight;
        const pdfImgWidth = pdfWidth - 20;
        const pdfImgHeight = pdfImgWidth / ratio;

        pdf.addImage(imgData, 'PNG', 10, 10, pdfImgWidth, pdfImgHeight);
        pdf.save(`summary-${activeTopicId}.pdf`);
      })
      .catch((err) => console.error('Error generating PDF:', err))
      .finally(() => setIsDownloading(false));
  };

  const handleShare = () => {
    const url = window.location.href;
    const text = `Check out my progress in the "${courseData.title}" course! I just finished the topic: "${
      allTopics.find((t) => t.id === activeTopicId)?.title
    }".`;
    if (navigator.share) {
      navigator.share({ title: 'Course Progress', text: text, url: url });
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      alert('Link copied to clipboard!');
    }
  };

  if (isLoading) return <div className="p-8 text-center">Loading summary...</div>;
  if (!summary) return <div className="p-8 text-center text-red-500">Failed to load summary.</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <div ref={summaryRef} className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg">
        <h2 className="text-3xl font-bold mb-6">
          Topic Summary: {allTopics.find((t) => t.id === activeTopicId)?.title}
        </h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-2 text-pink-600 dark:text-pink-400">Key Takeaways</h3>
            <ul className="list-disc list-inside space-y-1">
              {summary.points.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-semibold mb-2 text-pink-600 dark:text-pink-400">Actionable Steps</h3>
            <ul className="list-decimal list-inside space-y-1">
              {summary.steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
          <div className="border-l-4 border-pink-500 pl-4 italic text-gray-600 dark:text-gray-400">
            "{summary.quote}"
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-4">
        <button
          onClick={handleDownload}
          disabled={!scriptsLoaded || isDownloading}
          className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-white font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
          <Download size={18} />
          {isDownloading ? 'Downloading...' : 'Download as PDF'}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white font-semibold hover:bg-blue-700 transition-colors">
          <Share2 size={18} /> Share Progress
        </button>
      </div>
    </div>
  );
};

const MainContent = () => {
  const { activeTopicId, activeModule, setActiveModule, completedTopics, markTopicComplete } = useCourseStore();
  const activeTopic = allTopics.find((t) => t.id === activeTopicId);
  const isCompleted = completedTopics.has(activeTopicId);

  const navigateTopic = useCallback((direction) => {
    const { activeTopicId: currentTopicId } = useCourseStore.getState();
    const currentIndex = allTopics.findIndex((t) => t.id === currentTopicId);
    const nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < allTopics.length) {
      useCourseStore.getState().setActiveTopic(allTopics[nextIndex].id);
    }
  }, []);

  const modules = [
    { id: 'topic', label: 'Topic', icon: FileText },
    { id: 'wingman', label: 'AI Wingman', icon: MessageSquare },
    { id: 'flashcards', label: 'Flashcards', icon: ClipboardList },
    { id: 'summary', label: 'Summary', icon: BarChart },
  ];

  const moduleComponents = {
    topic: <TopicModule />,
    wingman: <WingmanModule />,
    flashcards: <FlashcardsModule />,
    summary: <SummaryModule />,
  };

  return (
    <div className="flex flex-1 flex-col min-h-0">
      <div className="sticky top-16 z-20 shrink-0 border-b border-gray-200 bg-white/90 p-4 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/90">
        <h2 className="text-2xl font-bold">{activeTopic.title}</h2>
        <div className="mt-4 border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-6 overflow-x-auto" role="tablist">
            {modules.map((m) => (
              <button
                key={m.id}
                id={`tab-${m.id}`}
                role="tab"
                aria-selected={activeModule === m.id}
                aria-controls={`panel-${m.id}`}
                onClick={() => setActiveModule(m.id)}
                className={`group inline-flex items-center gap-2 whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                  activeModule === m.id
                    ? 'border-pink-500 text-pink-600 dark:text-pink-400'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:border-gray-600 dark:hover:text-gray-300'
                }`}>
                <m.icon size={16} /> {m.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule}
            id={`panel-${activeModule}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeModule}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}>
            {moduleComponents[activeModule]}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="sticky bottom-0 z-10 flex shrink-0 flex-col items-center justify-between gap-4 border-t border-gray-200 bg-white/80 p-4 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/80 sm:flex-row">
        <div className="flex gap-4">
          <button
            onClick={() => navigateTopic(-1)}
            className="rounded-lg bg-gray-200 px-4 py-2 font-semibold hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600">
            Previous
          </button>
          <button
            onClick={() => navigateTopic(1)}
            className="rounded-lg bg-gray-200 px-4 py-2 font-semibold hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600">
            Next
          </button>
        </div>
        <button
          onClick={markTopicComplete}
          className={`flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 sm:w-auto ${
            isCompleted ? 'bg-green-600 hover:bg-green-700' : 'bg-pink-600 hover:bg-pink-700'
          }`}>
          <CheckCircle size={20} />
          <span>{isCompleted ? 'Completed' : 'Mark as Complete'}</span>
        </button>
      </div>
    </div>
  );
};

// --- MAIN APP COMPONENT ---

export default function App() {
  const { isHydrated, theme, activeTopicId, isMobileMenuOpen } = useCourseStore();

  useLayoutEffect(() => {
    // This effect now only runs once and doesn't rely on the store's theme value
    // The theme is set via the store's `useEffect` below
    const stored = localStorage.getItem('course-progress-storage');
    if (stored) {
      const { state } = JSON.parse(stored);
      if (state.theme === 'dark') {
        document.documentElement.classList.add('dark');
      }
    }
  }, []);

  useEffect(() => {
    // This effect correctly syncs the DOM with the theme state from the store
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'auto';
  }, [isMobileMenuOpen]);

  const navigateTopic = useCallback((direction) => {
    const { activeTopicId: currentTopicId } = useCourseStore.getState();
    const currentIndex = allTopics.findIndex((t) => t.id === currentTopicId);
    const nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < allTopics.length) {
      useCourseStore.getState().setActiveTopic(allTopics[nextIndex].id);
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') navigateTopic(1);
      if (e.key === 'ArrowLeft') navigateTopic(-1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateTopic]);

  if (!isHydrated) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="h-screen w-full font-sans text-gray-900 dark:text-gray-100">
      <div className="flex h-full bg-white dark:bg-gray-900">
        <Header />
        <div className="flex flex-1 pt-16">
          <Sidebar />
          <MainContent />
        </div>
      </div>
    </div>
  );
}
